/**
 * Resolver stagionale UNICO MM-GG (barche + escursioni).
 * Sostituisce il match per data assoluta: converte la data in MM-GG e usa
 * `mdInWindow` (gestisce il wrap dic→gen) sui `ranges` (o start_md/end_md).
 * Le righe arrivano dalla tabella `seasonal_prices` (Fase 1/1b unificazione).
 */

import { mdInWindow } from '@/lib/excursion-seasonal-slots';
import { deriveSeasonColumns } from '@/lib/season-slot-price';

type DecimalLike = number | string | { toNumber?: () => number } | null | undefined;

export interface SeasonalRow {
  name: string;
  start_md?: string | null;
  end_md?: string | null;
  ranges?: unknown; // jsonb: Array<{start_md,end_md}>
  price_per_day: DecimalLike;
  price_half_day?: DecimalLike;
  // Fasce prezzate (sorgente UNICA post-consolidamento). Se presenti e prezzate,
  // pricePerDay/priceHalfDay si derivano da qui; le colonne restano fallback legacy.
  // I prezzi degli slot devono arrivare già markup-ati come le colonne (stesso applyMarkup).
  slots?: unknown;
  /** Prezzi noleggio multi-giorno di questa stagione: [{min_days,max_days,price_total}]. */
  multiday_tiers?: unknown;
  // Prezzi per-unità (barche weekly/monthly): la stagione è "prezzata" per quella modalità
  // se la colonna corrispondente > 0. Solo-stagionale: niente fallback al prezzo base barca.
  price_per_week?: DecimalLike;
  price_per_month?: DecimalLike;
  priority?: number | null;
  is_active?: boolean | null;
}

/** Modalità di noleggio: determina QUALE prezzo rende "prenotabile" una stagione. */
export type RentalMode = 'daily' | 'hourly' | 'weekly' | 'monthly';

export interface SeasonalMatch {
  pricePerDay: number;
  priceHalfDay: number | null;
  /** Mezza-mattina / mezza-pomeriggio distinte (F2a). null → usa priceHalfDay (floor). */
  priceHalfMorning: number | null;
  priceHalfAfternoon: number | null;
  pricePerWeek: number | null;
  pricePerMonth: number | null;
  name: string;
}

function num(v: DecimalLike): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v) || 0;
  if (typeof v.toNumber === 'function') return v.toNumber();
  return Number(v) || 0;
}

/** YYYY-MM-DD → MM-DD. */
export function dateToMd(dateStr: string): string {
  return dateStr.length >= 10 ? dateStr.slice(5, 10) : dateStr;
}

function rangesOf(row: SeasonalRow): Array<{ start_md: string; end_md: string }> {
  const r = row.ranges;
  if (Array.isArray(r) && r.length) {
    return r
      .filter((x): x is { start_md: string; end_md: string } => !!x && typeof x.start_md === 'string' && typeof x.end_md === 'string');
  }
  if (row.start_md && row.end_md) return [{ start_md: row.start_md, end_md: row.end_md }];
  return [];
}

function rowMatchesMd(row: SeasonalRow, md: string): boolean {
  return rangesOf(row).some((r) => mdInWindow(md, r.start_md, r.end_md));
}

/**
 * Cascata stagioni-prezzo barca → azienda → (base). Analogo a
 * resolveEffectivePeriods per le fasce escursioni: la barca con stagioni
 * proprie le usa (override sostitutivo); altrimenti, se eredita, usa quelle
 * aziendali; altrimenti nessuna stagione (fallback al prezzo base a monte).
 */
export function resolveEffectiveSeasonalRows<T extends SeasonalRow>(args: {
  boatRows: T[] | null | undefined;
  companyRows: T[] | null | undefined;
  inherited: boolean;
}): T[] {
  const boat = args.boatRows || [];
  if (boat.length > 0) return boat;
  if (args.inherited) return args.companyRows || [];
  return [];
}

/**
 * Miglior prezzo stagionale per una data (YYYY-MM-DD). null = nessuna stagione (usa base).
 * Priority DESC su overlap.
 */
export function seasonalForDate(dateStr: string, rows: SeasonalRow[] | null | undefined, mode: RentalMode = 'daily'): SeasonalMatch | null {
  if (!rows || rows.length === 0) return null;
  const md = dateToMd(dateStr);
  const matching = rows
    .filter((s) => s.is_active !== false && rowMatchesMd(s, md))
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  if (matching.length === 0) return null;
  // Sorgente UNICA: le fasce prezzate (daily) o la colonna per-unità (weekly/monthly).
  // Una stagione NON prezzata PER LA MODALITÀ della barca non deve renderla prenotabile:
  // si salta e si prova la successiva; se nessuna è prezzata → null → NON prenotabile.
  for (const cand of matching) {
    const derived = deriveSeasonColumns(cand.slots);
    const pricePerDay = derived.price_per_day != null ? derived.price_per_day : num(cand.price_per_day);
    const pricePerWeek = num(cand.price_per_week);
    const pricePerMonth = num(cand.price_per_month);
    // Prezzo che "accende" la stagione per la modalità della barca.
    const modePrice = mode === 'weekly' ? pricePerWeek : mode === 'monthly' ? pricePerMonth : pricePerDay;
    if (modePrice <= 0) continue;
    // priceHalfDay null (non 0) quando non configurata → il motore applica il 60% del giorno.
    const halfCol = cand.price_half_day != null ? num(cand.price_half_day) : 0;
    const priceHalfDay = derived.price_half_day != null ? derived.price_half_day : (halfCol > 0 ? halfCol : null);
    return {
      pricePerDay,
      priceHalfDay,
      // Lati distinti dalle fasce (F2a). Fallback a priceHalfDay quando un lato non ha slot.
      priceHalfMorning: derived.price_half_morning ?? priceHalfDay,
      priceHalfAfternoon: derived.price_half_afternoon ?? priceHalfDay,
      pricePerWeek: pricePerWeek > 0 ? pricePerWeek : null,
      pricePerMonth: pricePerMonth > 0 ? pricePerMonth : null,
      name: cand.name,
    };
  }
  return null;
}

/**
 * Riga stagione RAW che copre la data (priority DESC). Serve per leggere campi
 * NON normalizzati da seasonalForDate (es. `multiday_tiers`). null = nessuna.
 */
export function seasonRowForDate<T extends SeasonalRow>(dateStr: string, rows: T[] | null | undefined): T | null {
  if (!rows || rows.length === 0) return null;
  const md = dateToMd(dateStr);
  const matching = rows
    .filter((s) => s.is_active !== false && rowMatchesMd(s, md))
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  return matching[0] || null;
}

/**
 * Prezzo/giorno-equivalente stagionale della PROSSIMA data utile da `from`.
 * Usato per il display "a partire da" del catalogo: la card moltiplica per la
 * durata unità (×7 weekly, ×30 monthly), quindi qui si ritorna il per-giorno equivalente
 * (weekly → price_per_week/7, monthly → price_per_month/30). Scansiona fino a 365 giorni.
 */
export function nextSeasonalPricePerDay(rows: SeasonalRow[] | null | undefined, fromDate: Date, mode: RentalMode = 'daily'): number | null {
  if (!rows || rows.length === 0) return null;
  const d = new Date(fromDate);
  for (let i = 0; i < 365; i++) {
    const md = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const m = seasonalForDate(`0000-${md}`, rows, mode);
    if (m) {
      if (mode === 'weekly') return m.pricePerWeek != null ? m.pricePerWeek / 7 : null;
      if (mode === 'monthly') return m.pricePerMonth != null ? m.pricePerMonth / 30 : null;
      return m.pricePerDay;
    }
    d.setDate(d.getDate() + 1);
  }
  return null;
}

export type LowestUnit = 'half_day' | 'day' | 'week' | 'month';
export interface LowestSeasonalPrice {
  price: number;
  unit: LowestUnit;
}

/**
 * Prezzo "a partire da": il MINIMO che un cliente paga per una singola
 * prenotazione, tra tutte le stagioni attive e le modalità abilitate.
 * La mezza giornata (se offerta) è tipicamente la più bassa. Sorgente:
 * slots → colonne (deriveSeasonColumns), MAI il prezzo base barca (morto).
 * Ritorna null se nessuna stagione ha un prezzo prenotabile per le modalità.
 */
export function lowestSeasonalPrice(
  rows: SeasonalRow[] | null | undefined,
  modes: RentalMode[] = ['daily'],
  includeHalfDay: boolean = true,
): LowestSeasonalPrice | null {
  if (!rows || rows.length === 0) return null;
  let best: LowestSeasonalPrice | null = null;
  const consider = (price: number, unit: LowestUnit) => {
    if (price > 0 && (!best || price < best.price)) best = { price, unit };
  };
  for (const r of rows) {
    if (r.is_active === false) continue;
    const derived = deriveSeasonColumns(r.slots);
    if (modes.includes('daily') || modes.includes('hourly')) {
      // includeHalfDay=false → card catalogo: mostra solo l'unità PIENA (giorno),
      // mai la mezza (che sarebbe il minimo e "abbasserebbe" il da-prezzo).
      if (includeHalfDay) {
        const half = derived.price_half_day ?? num(r.price_half_day);
        if (half > 0) consider(half, 'half_day');
      }
      const day = derived.price_per_day ?? num(r.price_per_day);
      consider(day, 'day');
    }
    if (modes.includes('weekly')) consider(num(r.price_per_week), 'week');
    if (modes.includes('monthly')) consider(num(r.price_per_month), 'month');
  }
  return best;
}

/**
 * Prezzo "da X" per la CARD catalogo: stagione IN CORSO (oggi), UNITÀ PIENA
 * (giorno/settimana/mese secondo la modalità barca), MAI mezza giornata/ora.
 * Fallback (oggi fuori da ogni stagione): minimo full-unit tra le stagioni.
 */
export function catalogFromPrice(
  rows: SeasonalRow[] | null | undefined,
  boatMode: RentalMode,
  boatModes: RentalMode[],
  todayStr: string,
): LowestSeasonalPrice | null {
  const today = seasonalForDate(todayStr, rows, boatMode);
  if (today) {
    const unit: LowestUnit = boatMode === 'weekly' ? 'week' : boatMode === 'monthly' ? 'month' : 'day';
    const price = boatMode === 'weekly' ? today.pricePerWeek
      : boatMode === 'monthly' ? today.pricePerMonth
      : today.pricePerDay; // daily/hourly → giorno intero (mai mezza/ora)
    if (price != null && price > 0) return { price, unit };
  }
  // Fallback: minimo full-unit tra le stagioni (esclude mezza/ora).
  return lowestSeasonalPrice(rows, boatModes, false);
}

export interface PointPrice { from_price: number; from_unit: LowestUnit; half_price: number | null; }

/**
 * Prezzo "da" di UN punto di partenza: usa SOLO le righe del suo layer
 * (`point_id === pointId`). Ritorna null se il punto non ha layer prezzato.
 * Stessa regola di catalogFromPrice (stagione corrente, unità piena) + mezza.
 * Prezzi NET: il chiamante applica il markup.
 */
export function pointFromPrice(
  rows: SeasonalRow[] | null | undefined,
  pointId: string,
  boatMode: RentalMode,
  boatModes: RentalMode[],
  todayStr: string,
): PointPrice | null {
  const layer = (rows || []).filter((r) => ((r as { point_id?: string | null }).point_id ?? null) === pointId);
  if (layer.length === 0) return null;
  const from = catalogFromPrice(layer, boatMode, boatModes, todayStr);
  if (!from) return null;
  const half = (boatMode === 'daily' || boatMode === 'hourly')
    ? (seasonalForDate(todayStr, layer, 'daily')?.priceHalfDay ?? null) : null;
  return { from_price: from.price, from_unit: from.unit, half_price: half != null && half > 0 ? half : null };
}
